import * as React from 'react';

import {Stack} from 'shells/layout/stack/stack';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {RichTextarea} from 'shells/rich-textarea/rich-textarea';
import {SavedSettingToast} from 'shells/saved-setting-toast/saved-setting-toast';
import type {SaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {Switch} from 'shells/switch/switch';
import {CharacterLimit} from 'shells/textfield/character-limit';
import {Body} from 'shells/typography/body';

import type {ChatSettingsLookAndFeelInput} from 'nutshell-graphql-types';

import {useDebouncedSaveField} from '../actions-options/use-debounced-save-field';
import {DisclaimerToolbar} from '../config-options/disclaimer-toolbar';
import {SAVE_STATE_KEYS} from '../consts';
import type {SaveStateKey} from '../consts';

import styles from './disclaimer-section.module.css';

export const DISCLAIMER_CHARACTER_LIMIT = 200;

// Helper function to get text length from HTML content
export const getTextLengthFromHtml = (html: string): number => {
    if (!html) return 0;
    // Remove HTML tags and get text content length
    const textContent = html.replace(/<[^>]*>/g, '').trim();

    return textContent.length;
};

type Props = {
    savingToastKey: SaveStateKey | null | undefined;
    toastState: SaveState;
    isLoading: boolean;
    isDisclaimerEnabled: boolean;
    setIsDisclaimerEnabled: (value: boolean) => void;
    disclaimerMessage: string;
    handleSave: (updates: ChatSettingsLookAndFeelInput, toastKey: SaveStateKey) => Promise<any>;
    handleDisclaimerSave: (htmlValue: string) => Promise<any>;
};

export function DisclaimerSection(props: Props) {
    const disclaimerToolbarId = React.useMemo(
        () => `disclaimer-toolbar-${Math.random().toString(36).substr(2, 9)}`,
        []
    );

    const {
        value: disclaimerMessage,
        onChange: setDisclaimerMessage,
        onBlur: onDisclaimerMessageBlur,
    } = useDebouncedSaveField({
        savedValue: props.disclaimerMessage,
        handleSave: (htmlValue) => props.handleDisclaimerSave(htmlValue),
        validation: {
            customValidate: (htmlValue: string) => {
                const textLength = getTextLengthFromHtml(htmlValue);

                return textLength <= DISCLAIMER_CHARACTER_LIMIT;
            },
        },
    });

    const disclaimerTextLength = getTextLengthFromHtml(disclaimerMessage);

    return (
        <Stack spacing={16}>
            <Stack isHorizontal={true} spacing={8}>
                <SavedSettingToast
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.DISCLAIMER
                            ? props.toastState
                            : 'initial'
                    }
                >
                    <Body size='large' isBold={true}>
                        Disclaimer
                    </Body>
                </SavedSettingToast>
            </Stack>
            {props.isLoading ? (
                <Stack spacing={16}>
                    <LoadingText width={250} height={30} />
                    <LoadingText isFullWidth={true} height={100} />
                </Stack>
            ) : (
                <Stack spacing={16}>
                    <Stack isHorizontal={true} spacing={8}>
                        <Switch
                            onChange={() => {
                                props.setIsDisclaimerEnabled(!props.isDisclaimerEnabled);
                                props.handleSave(
                                    {
                                        disclaimerSettings: {
                                            isEnabled: !props.isDisclaimerEnabled,
                                        },
                                    },
                                    SAVE_STATE_KEYS.DISCLAIMER
                                );
                            }}
                            isRounded={true}
                            isOn={props.isDisclaimerEnabled}
                            isSmall={true}
                        />
                        <Body> {props.isDisclaimerEnabled ? 'On' : 'Off'}</Body>
                    </Stack>
                    <Stack spacing={8}>
                        <Body size='small' isBold={true}>
                            Disclaimer text
                        </Body>
                        <div>
                            <div className={styles['disclaimer-container']}>
                                <RichTextarea
                                    id='disclaimer-rich-textarea'
                                    value={disclaimerMessage}
                                    onChange={setDisclaimerMessage}
                                    onBlur={onDisclaimerMessageBlur}
                                    placeholder='Enter disclaimer text…'
                                    readOnly={!props.isDisclaimerEnabled}
                                    isFullHeight={true}
                                    errorMessage={
                                        disclaimerTextLength > DISCLAIMER_CHARACTER_LIMIT
                                            ? `Character limit of ${DISCLAIMER_CHARACTER_LIMIT} exceeded`
                                            : undefined
                                    }
                                    config={{
                                        modules: {
                                            toolbar: {
                                                container: `#${disclaimerToolbarId}`,
                                            },
                                        },
                                        formats: ['bold', 'italic', 'link'],
                                        images: false,
                                        formatLinks: true,
                                        useDivs: true,
                                        placeholders: false,
                                        toolbarId: disclaimerToolbarId,
                                    }}
                                    hasBorder={true}
                                />
                            </div>
                            <DisclaimerToolbar id={disclaimerToolbarId} />
                            <div className='mb-16 mt-8 flex justify-end'>
                                <CharacterLimit
                                    maximumCharacters={DISCLAIMER_CHARACTER_LIMIT}
                                    length={disclaimerTextLength}
                                />
                            </div>
                        </div>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}
